// Customer-site locale resolution. Pure (no window/navigator access) so the premium embed and
// a plain `node` sanity check can import it.
//
// Precedence:
//   1. a supported `?lang=` (hreflang alternates, shared links)
//   2. the tenant's default customer language (back office setting; overrides the browser)
//   3. the first supported browser language (only when the tenant default is null/automatic)
//   4. 'en'
// An explicit header switch happens after start-up and is not persisted, so it is not an input.

export const SUPPORTED_LOCALES = ['en', 'de', 'fr', 'it', 'sk', 'pt', 'hu']

// 'de-AT' / 'DE' → 'de'; anything unsupported → null
export function normalizeLocale(value) {
  if (typeof value !== 'string') return null
  const base = value.trim().split(/[-_]/)[0].toLowerCase()
  return SUPPORTED_LOCALES.includes(base) ? base : null
}

/**
 * @param {{ urlLang?: string|null, tenantDefault?: string|null, browserLangs?: string[]|string|null }} input
 * @returns {'en'|'de'|'fr'|'it'|'sk'|'pt'|'hu'}
 */
export function resolveInitialLocale({ urlLang = null, tenantDefault = null, browserLangs = [] } = {}) {
  // ?lang= must be an exact code, as before (hreflang links always use the bare code)
  const fromUrl = typeof urlLang === 'string' ? urlLang.trim().toLowerCase() : null
  if (fromUrl && SUPPORTED_LOCALES.includes(fromUrl)) return fromUrl

  const fromTenant = normalizeLocale(tenantDefault)
  if (fromTenant) return fromTenant

  const list = Array.isArray(browserLangs) ? browserLangs : [browserLangs]
  for (const lang of list) {
    const hit = normalizeLocale(lang)
    if (hit) return hit
  }
  return 'en'
}

// Browser-side inputs for resolveInitialLocale (safe outside a browser).
export function readUrlLang() {
  if (typeof window === 'undefined') return null
  return new URLSearchParams(window.location.search).get('lang')
}

// Only the primary browser language, as before (not the whole navigator.languages list).
export function readBrowserLangs() {
  if (typeof navigator === 'undefined') return []
  return [navigator.language || navigator.userLanguage].filter(Boolean)
}
